import { DeleteOutlined, EditOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

const { Title, Text } = Typography;

interface SponsorDelegatesProps {
  sponsorId: string | null;
  sponsorName?: string;
  visible: boolean;
  onClose: () => void;
}

interface Invite {
  id: string;
  email: string;
  status: 'INVITED' | 'REGISTERED' | 'REVOKED';
  invitedAt: string;
  registeredAt: string | null;
  ticketId: string | null;
  // #148 óta a soron áll a személy és a hely fajtája is.
  paid?: boolean;
  source?: 'ADMIN_INVITE' | 'SPONSOR_PAGE';
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  Registration?: { id: string; status: string } | null;
  Ticket?: {
    id: string;
    ticketNumber: string;
    firstName: string;
    lastName: string;
    phone?: string;
  } | null;
}

interface DelegatesData {
  /** A #148 közös keretszámítása — a bónusz és a túlfoglalás ebből él. */
  allowance?: {
    free: number;
    paidExtra: number;
    remainingFree: number;
    remainingPaid: number;
    waived: boolean;
    overbooked: boolean;
  };
  config?: { selfService: boolean; paidExtraTickets: boolean };
  sponsor: {
    id: string;
    name: string;
    bonusFreeTickets?: number;
    baseTicketCount: number;
    extraTicketsAllowed: number;
    extraTicketsRequested: number;
    extraTicketsApproved: number;
  };
  slots: {
    base: number;
    extraAllowed: number;
    extraRequested: number;
    extraApproved: number;
    effectiveMax: number;
    occupied: number;
    available: number;
  };
  invites: Invite[];
}

const STATUS_COLORS: Record<Invite['status'], string> = {
  INVITED: 'blue',
  REGISTERED: 'green',
  REVOKED: 'default',
};

const SponsorDelegates: React.FC<SponsorDelegatesProps> = ({
  sponsorId,
  sponsorName,
  visible,
  onClose,
}) => {
  const { t } = useTranslation();
  // Témázott megerősítő és üzenetek (#145).
  const { modal, message } = AntdApp.useApp();
  const [data, setData] = useState<DelegatesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailsInput, setEmailsInput] = useState('');
  const [sending, setSending] = useState(false);
  const [requestedEdit, setRequestedEdit] = useState<number>(0);
  const [bonusEdit, setBonusEdit] = useState<number>(0);
  const [personModal, setPersonModal] = useState<{
    mode: 'edit' | 'replace';
    invite: Invite;
  } | null>(null);
  const [personForm] = Form.useForm();

  useEffect(() => {
    if (visible && sponsorId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, sponsorId]);

  const load = async () => {
    if (!sponsorId) return;
    setLoading(true);
    try {
      const r = await api.get(`/sponsors/${sponsorId}/delegates`);
      setData(r.data);
      setRequestedEdit(r.data.slots.extraRequested);
      setBonusEdit(r.data.sponsor?.bonusFreeTickets ?? 0);
    } catch (e: any) {
      message.error(e.response?.data?.message || t('sponsors.delegates.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const parseEmails = (raw: string) =>
    raw
      .split(/[\s,;\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && /\S+@\S+\.\S+/.test(s));

  const sendInvites = async () => {
    if (!sponsorId) return;
    const emails = parseEmails(emailsInput);
    if (emails.length === 0) {
      message.warning(t('sponsors.delegates.emailRequired'));
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/sponsors/${sponsorId}/delegates`, { emails });
      const created = r.data?.results?.filter((x: any) => x.status === 'created').length || 0;
      const dup = r.data?.results?.filter((x: any) => x.status === 'duplicate').length || 0;
      // A backend `failed`-et ad arra a címre, amelynél a levélküldés elhasalt,
      // és a meghívó sorát vissza is veszi. Enélkül a kudarc „0 meghívó
      // kiküldve"-ként, indoklás nélkül jelenne meg.
      //
      // A CÍMEKET NEVEZZÜK MEG, nem a darabszámot. A szervezőnek a „2 címre nem
      // sikerült" használhatatlan: nem tudja, melyik kettőt kell újrapróbálnia,
      // és a sorok sem jönnek létre, tehát a listából sem derül ki. A válasz
      // címenként hordozza az állapotot, a köteget pedig a szponzor kerete
      // korlátozza, tehát a felsorolás rövid marad.
      const failedEmails: string[] = (r.data?.results ?? [])
        .filter((x: any) => x.status === 'failed')
        .map((x: any) => x.email);
      const failed = failedEmails.length;
      const note =
        (dup > 0 ? t('sponsors.delegates.duplicatesSkipped', { count: dup }) : '') +
        (failed > 0
          ? t('sponsors.delegates.invitesFailed', {
              count: failed,
              emails: failedEmails.join(', '),
            })
          : '');
      const summary = t('sponsors.delegates.invitesSent', { count: created }) + note;
      if (failed > 0) message.warning(summary);
      else message.success(summary);
      setEmailsInput('');
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || t('sponsors.delegates.inviteFailed'));
    } finally {
      setSending(false);
    }
  };

  const resend = async (inviteId: string) => {
    if (!sponsorId) return;
    try {
      await api.post(`/sponsors/${sponsorId}/delegates/${inviteId}/resend`);
      message.success(t('sponsors.delegates.inviteResent'));
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || t('sponsors.delegates.failed'));
    }
  };

  const requestExtra = async () => {
    if (!sponsorId) return;
    try {
      await api.post(`/sponsors/${sponsorId}/request-extra`, { count: requestedEdit });
      message.success(t('sponsors.delegates.requestUpdated'));
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || t('sponsors.delegates.failed'));
    }
  };

  const approveExtra = async () => {
    if (!(sponsorId && data)) return;
    try {
      await api.post(`/sponsors/${sponsorId}/approve-extra`, { count: data.slots.extraRequested });
      message.success(t('sponsors.delegates.extraApproved'));
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || t('sponsors.delegates.failed'));
    }
  };

  /** Alkuban adott plusz ingyenes hely — a szponzor oldalán azonnal látszik. */
  const saveBonus = async () => {
    if (!sponsorId) return;
    try {
      await api.patch(`/sponsors/${sponsorId}`, { bonusFreeTickets: bonusEdit });
      message.success(t('sponsors.delegates.saved'));
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || t('sponsors.delegates.failed'));
    }
  };

  const openPerson = (mode: 'edit' | 'replace', invite: Invite) => {
    setPersonModal({ mode, invite });
    personForm.setFieldsValue(
      mode === 'edit'
        ? {
            firstName: invite.firstName ?? invite.Ticket?.firstName ?? '',
            lastName: invite.lastName ?? invite.Ticket?.lastName ?? '',
            phone: invite.phone ?? '',
            jobTitle: invite.jobTitle ?? '',
          }
        : { firstName: '', lastName: '', email: '', phone: '', jobTitle: '' }
    );
  };

  const submitPerson = async (values: Record<string, unknown>) => {
    if (!(sponsorId && personModal)) return;
    const { invite, mode } = personModal;
    try {
      if (mode === 'edit') {
        // Az e-mail cím SZÁNDÉKOSAN nincs az űrlapon: az azonosítja a sort és
        // az hordozza a kiküldött jegylevelet — a cseréje másik személy.
        await api.patch(`/sponsors/${sponsorId}/delegates/${invite.id}`, values);
        message.success(t('sponsors.delegates.saved'));
      } else {
        await api.post(`/sponsors/${sponsorId}/delegates/${invite.id}/replace`, values);
        message.success(t('sponsors.delegates.replaced'));
      }
      setPersonModal(null);
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || t('sponsors.delegates.failed'));
    }
  };

  const removePerson = (invite: Invite) => {
    modal.confirm({
      title: t('sponsors.delegates.confirmRemoveTitle'),
      content: t('sponsors.delegates.confirmRemoveBody'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!sponsorId) return;
        try {
          await api.delete(`/sponsors/${sponsorId}/delegates/${invite.id}`);
          message.success(t('sponsors.delegates.removed'));
          load();
        } catch (e: any) {
          message.error(e.response?.data?.message || t('sponsors.delegates.failed'));
        }
      },
    });
  };

  /**
   * Az állapot felirata — LITERÁL szótárkulcsokkal, mert a panel kulcsai
   * típusosak.
   *
   * A fizetős sor állapotát a JELENTKEZÉS dönti el, nem a meghívóé: az
   * ilyenkor is `REGISTERED`, tehát önmagában „regisztrált"-at mutatna egy
   * helyre, amiért még nem fizettek.
   */
  const statusLabel = (r: Invite): string => {
    if (r.status === 'REVOKED') return t('sponsors.delegates.statusRevoked');
    if (r.paid && r.Registration) {
      return r.Registration.status === 'PAID'
        ? t('sponsors.delegates.statusPaid')
        : t('sponsors.delegates.statusAwaitingPayment');
    }
    if (r.status === 'REGISTERED') return t('sponsors.delegates.statusRegistered');
    return t('sponsors.delegates.statusInvited');
  };

  const statusColor = (r: Invite): string => {
    if (r.status === 'REVOKED') return 'default';
    if (r.paid && r.Registration) {
      return r.Registration.status === 'PAID' ? 'green' : 'orange';
    }
    return STATUS_COLORS[r.status];
  };

  const columns: ColumnsType<Invite> = [
    { title: t('sponsors.delegates.columnEmail'), dataIndex: 'email' },
    {
      title: t('sponsors.delegateStatus'),
      key: 'status',
      render: (_, r) => <Tag color={statusColor(r)}>{statusLabel(r)}</Tag>,
    },
    {
      title: t('sponsors.delegates.columnSource'),
      key: 'source',
      render: (_, r) =>
        r.source === 'SPONSOR_PAGE'
          ? t('sponsors.delegates.sourceSponsorPage')
          : t('sponsors.delegates.sourceAdminInvite'),
    },
    {
      title: t('common.name'),
      // A név elsődlegesen a MEGHÍVÓN áll — a szerkesztés is oda ír, és az
      // export is onnan olvassa. A jegy csak tartalék: a szponzor-oldalról
      // beküldött sorokon nincs jegy-hivatkozás, és ott a puszta jegy-olvasás
      // „—"-t mutatna arra az emberre, akinek az export a nevét kiírja.
      render: (_, r) => {
        const first = r.firstName ?? r.Ticket?.firstName ?? '';
        const last = r.lastName ?? r.Ticket?.lastName ?? '';
        return `${first} ${last}`.trim() || '—';
      },
    },
    {
      title: t('sponsors.delegates.columnPhone'),
      render: (_, r) => r.phone ?? r.Ticket?.phone ?? '—',
    },
    {
      title: t('sponsors.delegates.columnJobTitle'),
      render: (_, r) => r.jobTitle ?? '—',
    },
    {
      title: t('sponsors.delegates.columnTicketNumber'),
      render: (_, r) => r.Ticket?.ticketNumber ?? '—',
    },
    {
      title: t('sponsors.delegates.columnSubmitted'),
      render: (_, r) => (r.registeredAt ? dayjs(r.registeredAt).format('YYYY-MM-DD') : '—'),
    },
    {
      title: t('sponsors.delegates.columnInvitedAt'),
      dataIndex: 'invitedAt',
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('sponsors.delegates.columnAction'),
      // #158 — négy művelet fér el itt (újraküldés, szerkesztés, csere,
      // törlés), és ez a modál legfontosabb oszlopa. Vízszintes görgetés
      // közben is elérhető kell maradjon, különben a szervezőnek a tábla
      // végéig kell húznia minden egyes sornál.
      fixed: 'right',
      width: 260,
      render: (_, r) =>
        // A visszavont soron nincs mit tenni: a jegye érvénytelen, a helye
        // felszabadult. A többinél MINDHÁROM művelet elérhető — a mai kód a
        // regisztrált sort épp kihagyta, ezért küldte a kézikönyv a szervezőt
        // a Jegyek oldalra.
        r.status === 'REVOKED' ? null : (
          <Space>
            {r.status === 'INVITED' && (
              <Button size="small" icon={<ReloadOutlined />} onClick={() => resend(r.id)}>
                {t('sponsors.delegates.resend')}
              </Button>
            )}
            <Button size="small" icon={<EditOutlined />} onClick={() => openPerson('edit', r)}>
              {t('sponsors.delegates.edit')}
            </Button>
            <Button size="small" icon={<SwapOutlined />} onClick={() => openPerson('replace', r)}>
              {t('sponsors.delegates.replace')}
            </Button>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removePerson(r)}>
              {t('sponsors.delegates.remove')}
            </Button>
          </Space>
        ),
    },
  ];

  /**
   * A modális számai a #148 KÖZÖS keretszámításából jönnek, hogy az összesítő,
   * a lista oszlopa, a szponzor saját oldala és ez a modális ugyanazt mondja.
   *
   * A `slots` tartalék azért marad, mert az `allowance` a válaszban opcionális:
   * régebbi backend mellett a mai számokat rajzoljuk, nem üres csempét. A
   * `slots.occupied` viszont a FIZETŐS sorokat is számolja, ezért ott csak a
   * teljes hiány esetén esünk rá vissza.
   */
  const allowance = data?.allowance;
  const freeEntitled = allowance?.free ?? data?.slots.effectiveMax ?? 0;
  const freeAvailable = allowance?.remainingFree ?? data?.slots.available ?? 0;
  const occupiedFree = allowance
    ? Math.max(0, allowance.free - allowance.remainingFree)
    : (data?.slots.occupied ?? 0);
  const paidSeats = allowance?.paidExtra ?? 0;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={t('sponsors.delegates.title', {
        sponsor: sponsorName || data?.sponsor.name || '',
      })}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('common.close')}
        </Button>,
      ]}
      width={1000}
    >
      {data && (
        <>
          {/*
            A négy csempe a KÖZÖS keretszámításból él (#148 `computeAllowance`),
            nem a régi `slots.*` aritmetikából.

            MI VOLT A HIBA. A régi számok a bónuszjegyet kihagyták, a fizetős
            sorokat viszont beleszámolták a foglaltakba — ugyanarra a
            szponzorra a modális „Foglalva 4/5, Szabad 1"-et mutatott, míg az
            összesítő, a lista oszlopa és a szponzor saját oldala 6/3-at. A
            szervező ITT dönti el, hívhat-e még valakit.

            A `slots` tartalék azért marad, mert a válasz `allowance` mezője a
            típusban opcionális; egy régebbi backend mellett a mai számok
            jönnek, nem üres csempe.
          */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={5}>
              <Card size="small">
                <Statistic
                  title={t('sponsors.delegates.slotsBase')}
                  value={freeEntitled}
                  suffix={t('sponsors.delegates.ticketSuffix', { count: freeEntitled })}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card size="small">
                <Statistic
                  title={t('sponsors.delegates.slotsPaidExtra')}
                  value={paidSeats}
                  suffix={t('sponsors.delegates.ticketSuffix', { count: paidSeats })}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card size="small">
                <Statistic
                  title={t('sponsors.delegates.slotsOccupied')}
                  value={occupiedFree}
                  suffix={`/ ${freeEntitled}`}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card size="small">
                <Statistic
                  title={t('sponsors.delegates.slotsAvailable')}
                  value={freeAvailable}
                  valueStyle={{ color: freeAvailable > 0 ? '#52c41a' : '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>

          {data.allowance?.overbooked && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('sponsors.delegates.overbookedWarning', {
                issued: data.slots.occupied,
                allowed: data.allowance.free,
              })}
            />
          )}

          {data.config?.paidExtraTickets ? (
            /* Bekapcsolt fizetős helyeknél a kérés/jóváhagyás kártyát a
               BÓNUSZ váltja: az „extra" ilyenkor fizetős, és a szponzor a
               saját oldaláról veszi fel — a szervező itt az alkuban adott
               INGYENES pluszt adja meg. */
            <Card
              size="small"
              title={t('sponsors.delegates.bonusCard')}
              style={{ marginBottom: 16 }}
            >
              <Space wrap>
                <Text>{t('sponsors.delegates.bonusLabel')}</Text>
                <InputNumber
                  min={0}
                  value={bonusEdit}
                  onChange={(v) => setBonusEdit(Number(v ?? 0))}
                />
                <Button onClick={saveBonus}>{t('common.save')}</Button>
                <Text type="secondary">{t('sponsors.delegates.bonusHint')}</Text>
              </Space>
            </Card>
          ) : (
            <Card
              size="small"
              title={t('sponsors.delegates.extraCard')}
              style={{ marginBottom: 16 }}
            >
              {data.slots.extraAllowed === 0 ? (
                <Text type="secondary">{t('sponsors.delegates.noExtraAllowed')}</Text>
              ) : (
                <>
                  {data.slots.extraRequested > data.slots.extraApproved && (
                    <Alert
                      type="warning"
                      showIcon
                      message={t('sponsors.delegates.pendingApproval', {
                        count: data.slots.extraRequested - data.slots.extraApproved,
                      })}
                      style={{ marginBottom: 12 }}
                    />
                  )}
                  <Space wrap>
                    <Space>
                      <Text>{t('sponsors.delegates.requestedExtra')}</Text>
                      <InputNumber
                        min={0}
                        max={data.slots.extraAllowed}
                        value={requestedEdit}
                        onChange={(v) => setRequestedEdit(Number(v ?? 0))}
                        addonAfter={`/ max ${data.slots.extraAllowed}`}
                      />
                      <Button onClick={requestExtra}>{t('common.update')}</Button>
                    </Space>
                    <Space>
                      <Text>{t('sponsors.delegates.approvedLabel')}</Text>
                      <Text strong>{data.slots.extraApproved}</Text>
                      {data.slots.extraRequested > data.slots.extraApproved && (
                        <Button type="primary" onClick={approveExtra}>
                          {t('sponsors.delegates.approve', {
                            n: data.slots.extraRequested,
                          })}
                        </Button>
                      )}
                    </Space>
                  </Space>
                </>
              )}
            </Card>
          )}

          <Card
            size="small"
            title={t('sponsors.delegates.newInvites')}
            style={{ marginBottom: 16 }}
          >
            <Input.TextArea
              rows={3}
              value={emailsInput}
              onChange={(e) => setEmailsInput(e.target.value)}
              placeholder={t('sponsors.delegates.emailsPlaceholder')}
              // A KÖZÖS keretből, nem a régi `slots`-ból: bónuszjeggyel a
              // `slots.available` nulla lehet úgy, hogy valójában van szabad
              // ingyenes hely — a szervező ilyenkor nem tudott meghívni senkit.
              disabled={freeAvailable === 0}
            />
            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                loading={sending}
                onClick={sendInvites}
                disabled={freeAvailable === 0}
              >
                {t('sponsors.delegates.sendInvites')}
              </Button>
              <Text type="secondary">
                {t('sponsors.delegates.slotsFree', { count: freeAvailable })}
              </Text>
            </Space>
          </Card>

          <Card size="small" title={t('sponsors.delegates.card', { n: data.invites.length })}>
            <Table
              rowKey="id"
              size="small"
              dataSource={data.invites}
              columns={columns}
              loading={loading}
              pagination={false}
              // #158 — a #150 óta tíz oszlop van ezen a táblán (Forrás,
              // Telefon, Titulus, Beküldve), a sor-akciók pedig egyről négyre
              // nőttek. `scroll.x` nélkül a tábla nem görget, hanem kilóg a
              // `width={1000}` modálból: az Akció cella külön fehér dobozként
              // a modál széle fölé csúszott, rá a mögötte lévő oldalra.
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}
      <Modal
        open={!!personModal}
        onCancel={() => setPersonModal(null)}
        title={t(
          personModal?.mode === 'replace'
            ? 'sponsors.delegates.replaceTitle'
            : 'sponsors.delegates.editTitle'
        )}
        footer={null}
        destroyOnHidden
      >
        <Text type="secondary">
          {t(
            personModal?.mode === 'replace'
              ? 'sponsors.delegates.replaceHint'
              : 'sponsors.delegates.editHint'
          )}
        </Text>
        <Form form={personForm} layout="vertical" onFinish={submitPerson} style={{ marginTop: 12 }}>
          <Form.Item
            name="firstName"
            label={t('registrations.firstName')}
            rules={personModal?.mode === 'replace' ? [{ required: true }] : undefined}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t('registrations.lastName')}
            rules={personModal?.mode === 'replace' ? [{ required: true }] : undefined}
          >
            <Input />
          </Form.Item>
          {personModal?.mode === 'replace' && (
            <Form.Item
              name="email"
              label={t('common.email')}
              rules={[{ required: true, type: 'email' }]}
            >
              <Input type="email" />
            </Form.Item>
          )}
          <Form.Item name="phone" label={t('common.phone')}>
            <Input />
          </Form.Item>
          <Form.Item name="jobTitle" label={t('sponsors.delegates.columnJobTitle')}>
            <Input />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('common.save')}
              </Button>
              <Button onClick={() => setPersonModal(null)}>{t('common.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default SponsorDelegates;
